import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Sparkles, AlertCircle, Check, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Generate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [collections, setCollections] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const { data } = await axios.get(`${API}/collections`, { withCredentials: true });
        setCollections(data);
      } catch (err) {
        console.error("Failed to fetch collections", err);
      }
    };
    fetchCollections();
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      setError("Please enter a prompt");
      return;
    }

    // Check API key setup
    if (user?.api_key_preference === "personal" && !user?.personal_openai_key) {
      setError("Please set up your personal OpenAI API key in Settings");
      return;
    }

    setError("");
    setGenerating(true);
    setGeneratedImage(null);

    try {
      const { data } = await axios.post(
        `${API}/images/generate`,
        { 
          prompt: prompt.trim(),
          collection_id: collectionId || null
        },
        { withCredentials: true }
      );
      setGeneratedImage(data);
      toast.success("Image generated successfully!");
    } catch (err) {
      const errMsg = err.response?.data?.detail || "Failed to generate image";
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement("a");
    link.href = generatedImage.image_data;
    link.download = `tshirt-design-${generatedImage.id}.png`;
    link.click();
  };

  const promptExamples = [
    "A minimalist geometric mountain design in black and white",
    "Vintage surf wave illustration with sunset colors",
    "Abstract watercolor splashes with botanical elements",
    "Retro 80s neon grid with palm trees",
    "Hand-drawn coffee cup with steam swirls"
  ];

  return (
    <div className="max-w-4xl mx-auto pb-20 md:pb-0" data-testid="generate-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
          Generate Design
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Create AI-generated t-shirt designs using OpenAI
        </p>
      </div>

      {/* API Key Status */}
      <div className="mb-6 p-4 border border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs uppercase tracking-widest text-gray-500">Using:</span>
          <span className="font-medium">
            {user?.api_key_preference === "personal" ? "Personal OpenAI Key" : "Emergent Universal Key"}
          </span>
          {user?.api_key_preference === "personal" && !user?.personal_openai_key && (
            <span className="text-red-500 flex items-center gap-1">
              <AlertCircle size={14} /> Not configured
            </span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Form */}
        <div>
          <form onSubmit={handleGenerate} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2" data-testid="generate-error">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">
                Prompt
              </Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe your t-shirt design..."
                className="rounded-none border-gray-300 focus:border-black focus:ring-0 min-h-[120px] resize-none"
                data-testid="generate-prompt"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">
                Add to Collection (Optional)
              </Label>
              <Select value={collectionId || "none"} onValueChange={(val) => setCollectionId(val === "none" ? "" : val)}>
                <SelectTrigger className="rounded-none border-gray-300" data-testid="collection-select">
                  <SelectValue placeholder="Select a collection" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No collection</SelectItem>
                  {collections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={generating || !prompt.trim()}
              className="w-full rounded-none bg-black text-white hover:bg-gray-800 h-12"
              data-testid="generate-submit"
            >
              {generating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={16} className="mr-2" />
                  Generate Image
                </>
              )}
            </Button>
          </form>

          {/* Prompt Examples */}
          <div className="mt-8">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">
              Example Prompts
            </p>
            <div className="space-y-2">
              {promptExamples.map((example, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(example)}
                  className="block w-full text-left p-3 text-sm border border-gray-200 hover:border-black transition-colors"
                  data-testid={`example-prompt-${i}`}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">
            Preview
          </p>
          <div className="border border-gray-200 aspect-square bg-gray-50 flex items-center justify-center relative">
            {generating ? (
              <div className="text-center">
                <div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full spinner mx-auto mb-4" />
                <p className="text-sm text-gray-500">Generating your design...</p>
                <p className="text-xs text-gray-400 mt-1">This may take up to 60 seconds</p>
              </div>
            ) : generatedImage ? (
              <>
                <img
                  src={generatedImage.image_data}
                  alt={generatedImage.prompt}
                  className="w-full h-full object-contain"
                  data-testid="generated-image"
                />
              </>
            ) : (
              <div className="text-center p-8">
                <Sparkles className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-400 text-sm">Your generated design will appear here</p>
              </div>
            )}
          </div>

          {generatedImage && (
            <div className="mt-4 space-y-3">
              <div className="p-3 bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-2">
                <Check size={16} />
                Image saved to your library
                {generatedImage.collection_id && " and collection"}
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  className="flex-1 rounded-none"
                  data-testid="download-image"
                >
                  <Download size={16} className="mr-2" />
                  Download
                </Button>
                <Button
                  onClick={() => navigate("/images")}
                  variant="outline"
                  className="flex-1 rounded-none"
                  data-testid="view-library"
                >
                  View Library
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
